import { Component, resource, computed, inject, signal, viewChild, Signal, effect, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { map, of, switchMap } from 'rxjs';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { PasswordComponentValue, PasswordComponent } from '../controls/components/basic/password/password.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { GenericModalService, ModalSettingsMessage } from '../shared/generic-modal/generic-modal.service';
import { FileHttpService } from '../shared/http/file-http.service';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { User } from '../user/models';
import { CurrentUserService } from '../user/services/current-user.service';
import { FtpShowFilesComponent } from './ftp-show-files.component';
import { FTPAuthenticationType } from './models/ftp-authentication-type';
import { IntegratedFtpConfiguration } from './models/integrated-ftp-configuration';
import { IntegratedFtpValueContainer } from './models/integrated-ftp-value-container';
import { NewPartnerFtpComponent } from './new-partner-ftp.component';
import { PartnerComponentBase } from './partner-search-details.base';
import { PartnerFtpService } from './services/partner-ftp.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerOnboardingService } from './services/partner-onboarding.service';
import { TransportType } from '../partnerportal/models/partner-onboarding';

@Component({
  selector: 'ep-partner-details-ftp',
  templateUrl: './partner-search-details-ftp.component.html',
  styleUrl: './partner-search-details-ftp.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, NewPartnerFtpComponent, FtpShowFilesComponent, PasswordComponent, PermissionRequiredDirective]
})
export class PartnerSearchDetailsFtpComponent extends PartnerComponentBase {
  private configService = inject(ConfigurationService);
  private partnerFtpService = inject(PartnerFtpService);
  private modalService = inject(GenericModalService);
  private toastService = inject(ToastService);
  private loadingPanelService = inject(LoadingPanelService);
  private currentUserService = inject(CurrentUserService);
  private fileHttpService = inject(FileHttpService);
  private partnerOnboardingService = inject(PartnerOnboardingService);
  private newFtpComponent = viewChild(NewPartnerFtpComponent);
  private ftpPasswordComponent = viewChild<PasswordComponent>('ftpPwd');
  private currentUser: Signal<User | null> = toSignal(this.currentUserService.userChangedObserver, { initialValue: this.currentUserService.getCurrentUser() });

  currentIntegratedFtpConfiguration = signal<IntegratedFtpConfiguration>(null);
  currentPassword = linkedSignal<PasswordComponentValue>(() => ({ id: this.currentIntegratedFtpConfiguration()?.Password, value: null }));
  isCreatingNew = signal(false);
  showFilesVisible = signal(false);
  canChangeUsername = signal(false);
  showFilesbutton = signal(false);
  supportPublicKey = signal(false);
  showUpload = signal(false);
  showPasswordField = signal(true);
  changingPassword = signal(false);
  authenticationType = signal<string>(null);
  hasAnyFtpProvider = signal(false);
  ftpConnectionWorks = signal(true);
  ftpConnectionError = signal(null);

  passwordIsValid = toSignal(toObservable(computed(() => this.ftpPasswordComponent()?.control())).pipe(
    switchMap(c => c ? c.statusChanges.pipe(map(() => !!c.valid)) : of(false))
  ), { initialValue: false });

  allowShowPassword = computed(() => {
    const user = this.currentUser();
    if (user) {
      if (User.hasPermission('Partner_Action_FtpShowPassword', user)) return true;
      if (this.evaluatePermissions('Partner_Action_InternalPartner', user, User.hasPermission('Partner_Action_InternalPartner', user), this.selectedPartner)) return true;
    }
    return false;
  });

  ftpPluginNameResource = resource({
    params: () => ({}),
    loader: () => this.partnerFtpService.getPluginName()
  });
  ftpPluginName = computed(() => this.ftpPluginNameResource.value() ?? '');

  selectedSenderPartnerContactsResource = resource({
    params: () => ({}),
    loader: () => this.partnerFtpService.supportChangePassword()
  });
  supportChangePassword = computed(() => this.selectedSenderPartnerContactsResource.value() ?? false);

  create() {
    this.setEditMode(true);
    this.isCreatingNew.set(true);
    this.newFtpComponent().initialize(this.selectedPartner, this.supportPublicKey(), this.authenticationType(), this.showUpload(), this.showPasswordField());
  }

  cancelNew(): void {
    this.setEditMode(false);
    this.isCreatingNew.set(false);
  }

  ftpConfigurationSaved(integratedFtpConfiguration: IntegratedFtpConfiguration): void {
    this.selectedPartner.IntegratedFtpConfiguration = integratedFtpConfiguration;
    this.currentIntegratedFtpConfiguration.set(integratedFtpConfiguration);
    this.isCreatingNew.set(false);
    this.setEditMode(false);
  }

  toggleShowFiles(): void {
    this.showFilesVisible.set(!this.showFilesVisible());
  }

  getShowFilesButtonText(): string {
    return this.showFilesVisible() ? 'Hide Files' : 'Show Files';
  }

  async partnerSet(): Promise<void> {
    try {
      const hasFtpProvider = await this.partnerFtpService.hasAnyFTPProvider();
      this.hasAnyFtpProvider.set(hasFtpProvider);

      if (!hasFtpProvider) {
        return;
      }

      this.initializeFtpSettings();
      await this.loadSupportPublicKey();
    } catch (error) {
      this.ftpConnectionWorks.set(false);
      this.ftpConnectionError.set(error);
    }
  }

  async changeUsername() {
    const newUsername = await this.modalService.inputDirect('New username', 'Enter the new username');
    if (newUsername) {
      this.loadingPanelService.showLoadingPanelDirect('Changing username...');
      const exists = await this.partnerFtpService.usernameExists(newUsername);
      if (exists) {
        await this.modalService.messageDirect('Username in use', 'The given username is alread in use.');
        this.loadingPanelService.hideLoadingPanel();
      }
      else {
        const valueContainer = new IntegratedFtpValueContainer(this.selectedPartner.PartnersId, newUsername);
        await this.partnerFtpService.changeUsername(valueContainer);
        this.currentIntegratedFtpConfiguration().Username = newUsername;
        this.loadingPanelService.hideLoadingPanel();
        await this.modalService.messageDirect('Username changed', 'Username successfully changed.');
      }
    }
  }

  beginChangePassword(): void {
    this.setEditMode(true);
    this.changingPassword.set(true);
  }

  async endChangePassword(commit: boolean): Promise<void> {
    this.changingPassword.set(false);
    this.setEditMode(false);

    if (commit) {
      const pwd = this.currentPassword();
      if (pwd?.value) {
        try {
          this.loadingPanelService.showLoadingPanelDirect('Changing password...');
          const valueContainer = new IntegratedFtpValueContainer(this.selectedPartner.PartnersId, pwd?.value);
          await this.partnerFtpService.changePassword(valueContainer);
          this.toastService.showToast('Password changed...', ToastType.success);
          this.reloadDetailsData();
        }
        finally {
          this.loadingPanelService.hideLoadingPanel();
        }
      }
    }
    else {
      this.currentIntegratedFtpConfiguration.set({ ...this.currentIntegratedFtpConfiguration() });
    }
  }

  onPasswordChange(newValue: PasswordComponentValue): void {
    this.currentPassword.update(cp => ({ ...cp, value: newValue.value }));
  };

  async changePublicKey(): Promise<void> {
    if (!this.showUpload) {
      await this.modalService.messageDirect('Choose public key', 'Please choose a public key to upload.');
      return;
    }

    const files = await this.modalService.fileSelectorDirect(
      'Update Public Key',
      'Select the public key file to upload',
      false,
      'Upload'
    );

    if (!files || files.length === 0) {
      return;
    }

    this.loadingPanelService.showLoadingPanelDirect('Changing public key...');
    try {
      const fileResult = await this.uploadPublicKeyFile(files);
      const updateResult = await this.updatePublicKey(fileResult);

      if (updateResult) {
        await this.modalService.messageDirect('Public key changed', 'Public key successfully updated.');
      } else {
        await this.modalService.messageDirect('Upload public key error', 'Unable to update public key.');
      }
      this.loadingPanelService.hideLoadingPanel();
    } catch {
      this.loadingPanelService.hideLoadingPanel();
      await this.modalService.messageDirect('Upload public key error', 'Unable to upload or update the public key.');
    }
  }

  async setLocalFtp(include: boolean): Promise<void> {
    const title = include ? 'Include in Integrated Ftp Adapter' : 'Exclude from Integrated Ftp Adapter';
    const text = include
      ? 'Do you want to include this account in the Integrated Ftp Adapter?'
      : 'Do you want to exclude this account from the Integrated Ftp Adapter?';

    try {
      await this.modalService.confirmDirect(title, text, 'Yes', 'No');
      this.loadingPanelService.showLoadingPanelDirect('Updating Integrated Ftp configuration...');
      await this.partnerFtpService.setIntegratedFtp(this.selectedPartner.PartnersId, include);
      this.currentIntegratedFtpConfiguration().ExcludeFromIntegratedFtpRetrieve = !include;
    } catch {
      // User cancelled the confirmation dialog
    } finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  async delete() {
    await this.modalService.confirmDirect('Delete Ftp', 'Delete the Ftp account?');
    this.loadingPanelService.showLoadingPanelDirect('Deleting Ftp Account...');
    await this.partnerFtpService.deleteFtp(this.selectedPartner.PartnersId);
    await this.partnerOnboardingService.clearTransportCredentials(this.selectedPartner.PartnersId, [TransportType.IntegratedFTP, TransportType.IntegratedSFTP]);
    this.selectedPartner.IntegratedFtpConfiguration = null;
    this.currentIntegratedFtpConfiguration.set(null);
    this.loadingPanelService.hideLoadingPanel();
  }

  private initializeFtpSettings(): void {
    this.isCreatingNew.set(false);
    this.canChangeUsername.set(false);
    this.showFilesVisible.set(false);
    this.showFilesbutton.set(this.currentUserService.hasPermission('Partner_Action_FtpShowFiles'));
    this.currentIntegratedFtpConfiguration.set(this.selectedPartner.IntegratedFtpConfiguration);
  }

  private async loadSupportPublicKey(): Promise<void> {
    const supportPublicKey = await this.partnerFtpService.supportPublicKey();

    if (supportPublicKey) {
      const authType = await this.configService.getEffectiveSettingValue('FTPAuthenticationType');
      const normalizedAuthType = authType ? authType.toLowerCase() : FTPAuthenticationType.Password;

      this.authenticationType.set(normalizedAuthType);
      this.showUpload.set(normalizedAuthType.includes(FTPAuthenticationType.PublicKey));
      this.showPasswordField.set(normalizedAuthType.includes(FTPAuthenticationType.Password));
    }
  }

  private async uploadPublicKeyFile(files: File[]): Promise<string> {
    return this.fileHttpService.upload<string>('/api/partners/ftp/upload-public-key', files);
  }

  private async updatePublicKey(fileResult: string): Promise<boolean> {
    return this.partnerFtpService.updatePublicKey(
      this.selectedPartner.PartnersId,
      this.selectedPartner.IntegratedFtpConfiguration.Username,
      fileResult
    );
  }

  private createModalSettings(title: string, body: string): ModalSettingsMessage {
    const settings = new ModalSettingsMessage();
    settings.backdrop = 'static';
    settings.title = title;
    settings.body = body;
    return settings;
  }
}
